import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

function formatError(detail) {
  if (!detail) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthPage({ mode = "login" }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(mode);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'merchant', business_name: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === 'login') {
        const user = await login(form.email, form.password);
        toast.success('Connexion réussie !');
        navigate(user.role === 'admin' ? '/admin' : user.role === 'vendor' ? '/vendor' : '/dashboard');
      } else {
        const user = await register(form.email, form.password, form.name, form.role, form.business_name);
        toast.success('Compte créé avec succès !');
        navigate(user.role === 'admin' ? '/admin' : user.role === 'vendor' ? '/vendor' : '/dashboard');
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="min-h-screen flex items-center justify-center px-4 noise-bg" style={{ background: '#0F0F1A' }}>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="auth-logo">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center font-bold text-sm">S</div>
          <span className="font-bold text-2xl text-white" style={{ fontFamily: 'Outfit' }}>Spin Review</span>
        </Link>

        <div className="glass-card rounded-2xl p-8" data-testid="auth-card">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 mb-6 bg-white/5">
              <TabsTrigger value="login" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white" data-testid="auth-tab-login">
                Connexion
              </TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-[#7C3AED] data-[state=active]:text-white" data-testid="auth-tab-register">
                Inscription
              </TabsTrigger>
            </TabsList>

            <form onSubmit={handleSubmit}>
              <TabsContent value="register" className="space-y-4 mt-0">
                <div>
                  <Label className="text-[#E2E8F0] text-sm">Nom complet</Label>
                  <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jean Dupont" required
                    className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#94A3B8] focus:ring-[#7C3AED] focus:border-[#7C3AED]" data-testid="register-name-input" />
                </div>
                <div>
                  <Label className="text-[#E2E8F0] text-sm">Type de compte</Label>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    {[{ v: 'merchant', l: 'Commerçant' }, { v: 'vendor', l: 'Revendeur / Agence' }].map(r => (
                      <button key={r.v} type="button" onClick={() => set('role', r.v)}
                        className={`p-3 rounded-xl border text-sm font-medium transition-all ${form.role === r.v ? 'border-[#7C3AED] bg-[#7C3AED]/20 text-white' : 'border-white/10 text-[#94A3B8] hover:border-white/20'}`}
                        data-testid={`register-role-${r.v}`}>
                        {r.l}
                      </button>
                    ))}
                  </div>
                </div>
                {form.role === 'merchant' && (
                  <div>
                    <Label className="text-[#E2E8F0] text-sm">Nom du commerce</Label>
                    <Input value={form.business_name} onChange={e => set('business_name', e.target.value)} placeholder="Mon Restaurant"
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#94A3B8] focus:ring-[#7C3AED] focus:border-[#7C3AED]" data-testid="register-business-input" />
                  </div>
                )}
              </TabsContent>

              <div className="space-y-4">
                {tab === 'register' && <div className="h-0" />}
                <div>
                  <Label className="text-[#E2E8F0] text-sm">Email</Label>
                  <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@exemple.com" required
                    className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#94A3B8] focus:ring-[#7C3AED] focus:border-[#7C3AED]" data-testid="auth-email-input" />
                </div>
                <div>
                  <Label className="text-[#E2E8F0] text-sm">Mot de passe</Label>
                  <div className="relative">
                    <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)}
                      placeholder="••••••••" required minLength={6}
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-[#94A3B8] focus:ring-[#7C3AED] focus:border-[#7C3AED] pr-10" data-testid="auth-password-input" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-white">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full btn-primary rounded-xl py-5 text-base font-semibold" data-testid="auth-submit-btn">
                  {loading ? 'Chargement...' : tab === 'login' ? 'Se connecter' : 'Créer mon compte'}
                </Button>
              </div>
            </form>
          </Tabs>

          <p className="text-center text-sm text-[#94A3B8] mt-6">
            {tab === 'login' ? (
              <>Pas encore de compte ? <button onClick={() => setTab('register')} className="text-[#7C3AED] hover:underline" data-testid="auth-switch-register">S'inscrire</button></>
            ) : (
              <>Déjà un compte ? <button onClick={() => setTab('login')} className="text-[#7C3AED] hover:underline" data-testid="auth-switch-login">Se connecter</button></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
